import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Sparkles button that opens a small prompt dialog and calls /api/ai/letter-draft.
 * Calls `onDraft({ subject, body })` on success.
 * `context` is an optional object with { tenant_name, property_name, property_address, existing_body }.
 */
export default function AiDraftLetterButton({ context = {}, onDraft, size = "sm", label = "AI Draft" }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("professional");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error("Describe what the letter should say.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/letter-draft", {
        prompt: prompt.trim(),
        tone,
        tenant_name: context.tenant_name || undefined,
        property_name: context.property_name || undefined,
        property_address: context.property_address || undefined,
        existing_body: context.existing_body || undefined,
      });
      onDraft?.(data);
      toast.success("Draft ready");
      setOpen(false);
      setPrompt("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI draft failed");
    }
    setBusy(false);
  };

  return (
    <>
      <Button
        type="button"
        data-testid="ai-draft-letter-btn"
        variant="outline"
        size={size}
        onClick={() => setOpen(true)}
        className="gap-1.5 border-gold/50 text-ink hover:border-gold hover:bg-gold/5"
      >
        <Sparkles className="h-3.5 w-3.5 text-gold" />
        <span className="uppercase tracking-[0.15em] text-[10px] font-semibold">{label}</span>
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="ai-draft-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              <span className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-gold" /> Draft with AI
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="firm">Firm</SelectItem>
                  <SelectItem value="empathetic">Empathetic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                {context.existing_body ? "What would you like to change or refine?" : "What is this letter about?"}
              </Label>
              <Textarea
                data-testid="ai-prompt-input"
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Rent is 10 days overdue and a $50 late fee has been applied. Ask them to pay within 5 days."
              />
              {(context.tenant_name || context.property_name) && (
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">
                  Context —
                  {context.tenant_name && <> tenant: <b>{context.tenant_name}</b></>}
                  {context.property_name && <> · property: <b>{context.property_name}</b></>}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              type="button"
              data-testid="ai-generate-btn"
              onClick={generate}
              disabled={busy}
              className="rounded-none bg-ink text-ivory hover:bg-gold hover:text-ink uppercase tracking-[0.2em] text-xs font-semibold"
            >
              {busy ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Drafting…</> : <><Sparkles className="h-3.5 w-3.5 mr-2" /> Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
